// src/cockpit4/workspaces/KnowledgeWorkspace.tsx
// ============================================
// Knowledge Workspace - 知识库工作区
//
// 4 个子区域：Card / Idea / Research / Prompt
// 数据来源：暂用占位（KnowledgeRuntime / HermesRuntime 的 Growth Engine）
// ============================================

import { useEffect, useMemo, useState, useRef, useCallback, type CSSProperties } from "react";
import { motion, AnimatePresence } from "framer-motion";
// Phase 3 P0-3：页面顶部业务说明 + 跨模块跳转
import WorkspaceBizBar from "../components/WorkspaceBizBar";
import {
  BookOpen,
  Lightbulb,
  Microscope,
  MessageSquareCode,
  ArrowUpRight,
  FileText,
  Files,
  Share2,
  Folder,
  ChevronRight,
  ChevronDown,
  TrendingUp,
  Activity,
  Target,
  Database,
  RefreshCw,
  Loader2,
  Search,
  X,
  Brain,
  Sparkles,
  BarChart3,
  Wand2,
  Network,
  Map as MapIcon,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
// 知识查询枚举（运行时值，需 import 而非 import type）
import { KnowledgeQuery } from "../../runtime/KnowledgeRuntime/types";
// 企业知识资产统计类型（Program A Phase A）
import type {
  KnowledgeAssetStats,
  KnowledgeAssetCategory,
} from "../../runtime/KnowledgeRuntime/types";
// P0 知识中心新组件：智能问答 / 知识推荐 / 质量评估
import KnowledgeQA from "../components/KnowledgeQA";
import KnowledgeRecommendation from "../components/KnowledgeRecommendation";
import KnowledgeQualityPanel from "../components/KnowledgeQualityPanel";
// v6.23 内容权限：员工/游客禁复制 + 服务端按角色过滤敏感路径
import { canCopyContent } from "../../utils/vaultAccessGuard";
// P1 知识中心新组件：知识生产 / 反馈闭环 / 知识地图 / 版本协作
import KnowledgeProducer from "../components/KnowledgeProducer";
import OntologyPanel from "../components/OntologyPanel";
import KnowledgeFeedback from "../components/KnowledgeFeedback";
import KnowledgeMap from "../components/KnowledgeMap";
import KnowledgeVersion from "../components/KnowledgeVersion";
// 第十一个 Runtime：知识演化面板（第十五章 Knowledge Evolution Protocol）
import KnowledgeEvolutionPanel from "../components/KnowledgeEvolutionPanel";
// P2-功能8：智能培训系统
import SmartTrainingSystem from "../components/SmartTrainingSystem";
// P2-功能9：专家网络与问答社区
import ExpertNetwork from "../components/ExpertNetwork";
// P2-功能10：知识商业化
import KnowledgeMonetization from "../components/KnowledgeMonetization";
// v5.2 审计修复 P1：知识飞轮入口（软件操作→知识沉淀）
import KnowledgeFlywheelEntry from "../components/KnowledgeFlywheelEntry";

// ============================================
// 类型
// ============================================

interface KnowledgeCard {
  id: string;
  title: string;
  category: string;
  summary: string;
  date: string;
  filePath?: string;
  content?: string;
  tags?: string[];
  verified?: boolean;
}

interface KnowledgeIdea {
  id: string;
  title: string;
  category: string;
  summary: string;
  filePath?: string;
  content?: string;
  tags?: string[];
  date?: string;
}

interface KnowledgeResearch {
  id: string;
  title: string;
  status: "planning" | "in_progress" | "completed";
  progress: number;
}

interface KnowledgePrompt {
  id: string;
  name: string;
  description: string;
  category: string;
}

/** Vault 全部笔记（对应 VaultBridge.VaultNote） */
interface VaultNoteItem {
  id: string;
  title: string;
  filePath: string;
  fileName: string;
  category: string;
  mtime: string;
  size: number;
}

/** Vault 顶层目录覆盖率统计项（调用 /vault-api/list 获取） */
interface DirCoverageItem {
  /** 目录名（如 "09_Knowledge（知识中心）"） */
  name: string;
  /** 相对 Vault 根的路径 */
  path: string;
  /** 是否为目录 */
  isDirectory: boolean;
  /** 该目录直接 .md 文件数 */
  fileCount: number;
  /** 递归子目录的 .md 文件总数（含子目录） */
  totalFileCount: number;
}

/** 目录树节点（递归结构，用于左侧目录树展示） */
interface TreeNode {
  /** 节点名称（目录或文件名） */
  name: string;
  /** 相对 Vault 根的完整路径 */
  path: string;
  /** 是否为目录 */
  isDirectory: boolean;
  /** 子节点（仅目录有） */
  children: TreeNode[];
  /** 该目录下的直接笔记 */
  notes: VaultNoteItem[];
  /** 直接 .md 文件数 */
  fileCount: number;
  /** 递归 .md 文件总数（含子目录） */
  totalFileCount: number;
}

type SubTab = "cards" | "ideas" | "research" | "prompts" | "graph" | "all_notes";

/** 知识中心主区块（P0 + P1 + P2，在原有 SubTab 之上新增一层切换） */
type MainSection =
  // P0
  | "explorer"
  | "qa"
  | "recommendation"
  | "quality"
  // P1
  | "producer"
  | "feedback"
  | "map"
  | "version"
  // 第十一个 Runtime：知识演化（第十五章）
  | "evolution"
  // P2
  | "training"
  | "expertNetwork"
  | "monetization"
  // v5.2 审计修复 P1：知识飞轮入口（软件操作→知识自动沉淀）
  | "flywheel"
  // P0① 差距补齐：Ontology 企业地图（实体-关系索引）
  | "ontology";

/** P0 主区块导航配置 */
const P0_SECTIONS: { id: MainSection; label: string; icon: typeof BookOpen }[] = [
  { id: "explorer", label: "知识浏览", icon: BookOpen },
  { id: "qa", label: "智能问答", icon: Brain },
  { id: "recommendation", label: "知识推荐", icon: Sparkles },
  { id: "quality", label: "质量评估", icon: BarChart3 },
];

/** 进阶区块导航配置（v6 精简：仅保留真正高频的知识生产 + 知识地图） */
const P1_SECTIONS: { id: MainSection; label: string; icon: typeof BookOpen }[] = [
  { id: "producer", label: "知识生产", icon: Wand2 },
  { id: "map", label: "知识地图", icon: Network },
];

/** P2 区块导航配置（v6 精简：以下低频/概念重复功能从导航移除，代码保留）
 *  - 智能培训 training / 专家网络 expertNetwork：低频，与问答重复
 *  - 知识商业化 monetization：无实际业务支撑
 *  - 知识飞轮 flywheel：与知识地图/演化高度重叠
 *  - 反馈闭环 feedback / 版本协作 version / 知识演化 evolution：低频
 */
const P2_SECTIONS: { id: MainSection; label: string; icon: typeof BookOpen }[] = [];
// P0① 差距补齐：Ontology 企业地图（导航独立成组，紧邻知识地图）
const ONTO_SECTIONS: { id: MainSection; label: string; icon: typeof BookOpen }[] = [
  { id: "ontology", label: "企业地图", icon: MapIcon },
];

// v6.23 内容权限：员工/游客不可见的区块（管理职能，含全库质量评分与内容预览）
const EMPLOYEE_HIDDEN_SECTIONS: MainSection[] = ["quality"];

// ============================================
// 主组件
// ============================================

export default function KnowledgeWorkspace() {
  // 默认展示「全部笔记」，让用户看到完整 Obsidian Vault 内容（而非仅 Cards 子目录）
  const [subTab, setSubTab] = useState<SubTab>("all_notes");
  // P0 新增：主区块切换（知识浏览 / 智能问答 / 知识推荐 / 质量评估）
  const [activeSection, setActiveSection] = useState<MainSection>("explorer");
  const [cards, setCards] = useState<KnowledgeCard[]>([]);
  const [ideas, setIdeas] = useState<KnowledgeIdea[]>([]);
  const [research, setResearch] = useState<KnowledgeResearch[]>([]);
  const [prompts, setPrompts] = useState<KnowledgePrompt[]>([]);
  // Vault 全部笔记（覆盖整个 Obsidian Vault，不限于 Cards 子目录）
  const [allNotes, setAllNotes] = useState<VaultNoteItem[]>([]);
  const [loading, setLoading] = useState(true);
  // 选中的分类（用于 Cards 左侧目录树过滤）
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  // 选中的笔记分类（用于「全部笔记」左侧目录树过滤）
  const [selectedNoteCategory, setSelectedNoteCategory] = useState<string>("all");
  // 选中的卡片/灵感（用于详情预览面板）
  const [selectedCard, setSelectedCard] = useState<KnowledgeCard | null>(null);
  const [selectedIdea, setSelectedIdea] = useState<KnowledgeIdea | null>(null);
  // 选中的笔记（用于「全部笔记」详情预览面板）
  const [selectedNote, setSelectedNote] = useState<VaultNoteItem | null>(null);
  // 笔记详情内容（懒加载）
  const [noteContent, setNoteContent] = useState<{ loading: boolean; content: string; error?: string } | null>(null);
  // 当前点击高亮的资产分类 key（让用户感知 Tab 点击生效）
  const [activeAssetKey, setActiveAssetKey] = useState<string | null>(null);
  // Graph 数据
  const [graphData, setGraphData] = useState<any>(null);
  const [graphLoading, setGraphLoading] = useState(false);
  // 企业知识资产统计（Program A Phase A）
  const [assetStats, setAssetStats] = useState<KnowledgeAssetStats | null>(null);
  const [assetStatsLoading, setAssetStatsLoading] = useState(false);
  // 知识检索（Program A Phase A：所有 Runtime 第一步都是 Knowledge Search）
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<
    Array<{ title: string; path?: string; summary?: string; score?: number }>
  >([]);
  // Obsidian Vault 顶层目录覆盖率统计（任务2：调用 /vault-api/list 获取各目录文件数）
  const [dirCoverage, setDirCoverage] = useState<DirCoverageItem[]>([]);
  const [dirCoverageLoading, setDirCoverageLoading] = useState(false);

  // 加载知识资产统计
  const loadAssetStats = useCallback(async () => {
    setAssetStatsLoading(true);
    try {
      // 等 RuntimeManager 就绪
      for (let i = 0; i < 20; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      const resp = await RuntimeManager.query(RuntimeName.Knowledge, {
        type: KnowledgeQuery.AssetStats,
      });
      if (resp?.success && resp.data) {
        // resp.data 是 KnowledgeResult，需要再解包 data
        const inner = resp.data as { success: boolean; data?: KnowledgeAssetStats };
        if (inner.success && inner.data) {
          setAssetStats(inner.data);
        }
      }
    } catch (err) {
      console.warn("[KnowledgeWorkspace] AssetStats 查询失败:", err);
    } finally {
      setAssetStatsLoading(false);
    }
  }, []);

  // 知识检索（调用 KnowledgeQuery.Search）
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchResults([]);
    try {
      for (let i = 0; i < 20; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      const resp = await RuntimeManager.query(RuntimeName.Knowledge, {
        type: KnowledgeQuery.Search,
        options: { keyword: searchQuery.trim(), limit: 20 },
      });
      if (resp?.success && resp.data) {
        const inner = resp.data as { success: boolean; data?: any };
        if (inner.success && inner.data) {
          const results = Array.isArray(inner.data)
            ? inner.data
            : inner.data.results || inner.data.items || [];
          setSearchResults(
            results.slice(0, 20).map((r: any) => ({
              title: r.title || r.name || r.fileName || "未命名",
              path: r.path || r.folderName,
              summary: r.summary || r.content?.slice(0, 100),
              score: r.score,
            }))
          );
        }
      }
    } catch (err) {
      console.warn("[KnowledgeWorkspace] 检索失败:", err);
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  /**
   * 加载 Obsidian Vault 顶层目录覆盖率统计（任务2）
   *
   * 调用 /vault-api/list?dir=. 获取 Vault 根目录下的所有顶层目录，
   * 然后对每个顶层目录递归调用 /vault-api/list 统计 .md 文件数。
   * 用于"全部笔记"Tab 中显示目录覆盖率统计面板。
   */
  const loadDirCoverage = useCallback(async () => {
    setDirCoverageLoading(true);
    try {
      // 第一步：列出 Vault 根目录（dir=.）下所有顶层条目
      const rootResp = await fetch(`/vault-api/list?dir=.&ext=`);
      if (!rootResp.ok) {
        console.warn("[KnowledgeWorkspace] 目录覆盖率根列表请求失败:", rootResp.status);
        setDirCoverage([]);
        return;
      }
      const rootJson = await rootResp.json();
      if (!rootJson.success || !Array.isArray(rootJson.files)) {
        console.warn("[KnowledgeWorkspace] 目录覆盖率根列表解析失败:", rootJson);
        setDirCoverage([]);
        return;
      }
      // 仅取目录（文件不参与覆盖率统计）
      const topDirs = rootJson.files.filter((f: any) => f.isDirectory);
      if (topDirs.length === 0) {
        setDirCoverage([]);
        return;
      }
      // 第二步：并行获取每个顶层目录的递归 .md 文件数
      // 递归统计函数：深度优先遍历子目录，累加 .md 文件数
      const countRecursive = async (dirPath: string): Promise<{ direct: number; total: number }> => {
        try {
          const resp = await fetch(`/vault-api/list?dir=${encodeURIComponent(dirPath)}&ext=.md`);
          if (!resp.ok) return { direct: 0, total: 0 };
          const json = await resp.json();
          if (!json.success || !Array.isArray(json.files)) return { direct: 0, total: 0 };
          let direct = 0;
          let total = 0;
          const subDirs: string[] = [];
          for (const f of json.files) {
            if (f.isDirectory) {
              subDirs.push(f.path);
            } else {
              direct++;
              total++;
            }
          }
          // 递归子目录（限制深度避免无限递归，最多 3 层）
          const depth = dirPath.split('/').length;
          if (depth < 5) {
            const subResults = await Promise.all(subDirs.map((d) => countRecursive(d)));
            for (const r of subResults) {
              total += r.total;
            }
          }
          return { direct, total };
        } catch {
          return { direct: 0, total: 0 };
        }
      };
      // 并行统计所有顶层目录（控制并发避免请求过多）
      const items: DirCoverageItem[] = await Promise.all(
        topDirs.map(async (d: any) => {
          const { direct, total } = await countRecursive(d.path);
          return {
            name: d.name,
            path: d.path,
            isDirectory: true,
            fileCount: direct,
            totalFileCount: total,
          };
        })
      );
      // 按目录名排序（数字前缀优先）
      items.sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
      setDirCoverage(items);
    } catch (err) {
      console.warn("[KnowledgeWorkspace] 目录覆盖率加载失败:", err);
      setDirCoverage([]);
    } finally {
      setDirCoverageLoading(false);
    }
  }, []);

  // 切换到「全部笔记」Tab 时加载目录覆盖率（仅加载一次）
  useEffect(() => {
    if (subTab === "all_notes" && dirCoverage.length === 0 && !dirCoverageLoading) {
      void loadDirCoverage();
    }
  }, [subTab, dirCoverage.length, dirCoverageLoading, loadDirCoverage]);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: number | null = null;
    console.log("[KnowledgeWorkspace] useEffect 触发，开始加载");
    // 30 秒超时定时器：避免无限加载（中间件不可用或请求阻塞时自动停止）
    const timeoutId = window.setTimeout(() => {
      if (!cancelled) {
        console.warn("[KnowledgeWorkspace] 加载超时（30s），停止加载");
        setLoading(false);
      }
    }, 30000);

    // 等待 RuntimeManager 初始化完成（解决组件挂载早于 Runtime 就绪的竞态）
    const waitForRuntime = async (maxRetries = 20, intervalMs = 300): Promise<boolean> => {
      for (let i = 0; i < maxRetries; i++) {
        if (cancelled) return false;
        if (RuntimeManager.isInitialized()) return true;
        await new Promise((r) => { retryTimer = window.setTimeout(r, intervalMs); });
      }
      return false;
    };

    const loadAll = async () => {
      setLoading(true);
      try {
        // 等 RuntimeManager 就绪
        const ready = await waitForRuntime();
        if (cancelled || !ready) {
          setLoading(false);
          return;
        }
        console.log("[KnowledgeWorkspace] 开始并行查询 RuntimeManager");
        // 并行拉取 5 类数据（含 Research）+ Vault 全部笔记
        const [cardsResp, ideasResp, promptsResp, researchResp, allNotesResp] = await Promise.all([
          RuntimeManager.query(RuntimeName.Knowledge, {
            type: KnowledgeQuery.ListCards,
          }).catch((err) => {
            console.error("[KnowledgeWorkspace] cards 查询异常:", err);
            return null;
          }),
          RuntimeManager.query(RuntimeName.Knowledge, {
            type: KnowledgeQuery.ListIdeas,
          }).catch((err) => {
            console.error("[KnowledgeWorkspace] ideas 查询异常:", err);
            return null;
          }),
          RuntimeManager.query(RuntimeName.Prompt, { type: "list" }).catch(
            (err) => {
              console.error("[KnowledgeWorkspace] prompts 查询异常:", err);
              return null;
            }
          ),
          RuntimeManager.query(RuntimeName.Knowledge, {
            type: KnowledgeQuery.ListResearch,
          }).catch((err) => {
            console.error("[KnowledgeWorkspace] research 查询异常:", err);
            return null;
          }),
          RuntimeManager.query(RuntimeName.Knowledge, {
            type: KnowledgeQuery.ListAllNotes,
          }).catch((err) => {
            console.error("[KnowledgeWorkspace] all_notes 查询异常:", err);
            return null;
          }),
        ]);

        if (cancelled) {
          console.log("[KnowledgeWorkspace] 已取消，跳过 setCards");
          return;
        }

        console.log("[KnowledgeWorkspace] 查询结果:", {
          cards: { success: cardsResp?.success, dataLen: Array.isArray(cardsResp?.data) ? cardsResp.data.length : 'N/A', error: cardsResp?.error },
          ideas: { success: ideasResp?.success, dataLen: Array.isArray(ideasResp?.data) ? ideasResp.data.length : 'N/A', error: ideasResp?.error },
          prompts: { success: promptsResp?.success, dataLen: Array.isArray(promptsResp?.data) ? promptsResp.data.length : 'N/A', error: promptsResp?.error },
        });

        const extractedCards = extractList(cardsResp, []);
        const extractedIdeas = extractList(ideasResp, []);
        const extractedPrompts = extractList(promptsResp, []);
        const extractedResearch = extractList(researchResp, []);
        const extractedAllNotes = extractList(allNotesResp, []);
        console.log("[KnowledgeWorkspace] 提取后:", { cards: extractedCards.length, ideas: extractedIdeas.length, prompts: extractedPrompts.length, research: extractedResearch.length, allNotes: extractedAllNotes.length });

        setCards(extractedCards);
        setIdeas(extractedIdeas);
        setResearch(extractedResearch);
        setPrompts(extractedPrompts);
        setAllNotes(extractedAllNotes);
      } catch (err) {
        console.error("[KnowledgeWorkspace] 加载失败:", err);
        if (!cancelled) {
          setCards([]);
          setIdeas([]);
          setResearch([]);
          setPrompts([]);
          setAllNotes([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          window.clearTimeout(timeoutId);
          console.log("[KnowledgeWorkspace] 加载完成");
          // 同时加载知识资产统计
          loadAssetStats();
        }
      }
    };
    loadAll();
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [loadAssetStats]);

  // 切换到 Graph tab 时自动加载关系图谱（含 Runtime 就绪等待）
  useEffect(() => {
    if (subTab !== "graph" || graphData || graphLoading) return;

    let cancelled = false;
    let retryTimer: number | null = null;
    const waitAndLoad = async () => {
      // 等 RuntimeManager 就绪
      for (let i = 0; i < 20; i++) {
        if (cancelled) return;
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => { retryTimer = window.setTimeout(r, 300); });
      }
      if (cancelled) return;

      setGraphLoading(true);
      try {
        const resp = await RuntimeManager.query(RuntimeName.Knowledge, {
          type: KnowledgeQuery.Graph,
        });
        if (!cancelled && resp?.success && resp.data) {
          // resp.data 是 KnowledgeResult，需要再解包一层拿到 { nodes, edges }
          const inner = resp.data as { success?: boolean; data?: any };
          if (inner?.success && inner.data) {
            setGraphData(inner.data);
          } else if (inner && Array.isArray((inner as any).nodes)) {
            setGraphData(inner);
          }
        }
      } catch (err) {
        console.error("[KnowledgeWorkspace] Graph 查询失败:", err);
      } finally {
        if (!cancelled) setGraphLoading(false);
      }
    };
    waitAndLoad();
    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [subTab]);

  return (
    <div
      className="h-full w-full overflow-hidden p-4"
      // v6.23 内容权限：员工/游客禁复制知识内容（管理员正常）
      onCopy={(e) => {
        if (!canCopyContent()) {
          e.preventDefault();
        }
      }}
      onContextMenu={(e) => {
        if (!canCopyContent()) {
          e.preventDefault();
        }
      }}
      style={
        canCopyContent()
          ? undefined
          : ({ userSelect: "none", WebkitUserSelect: "none" } as CSSProperties)
      }
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
              <BookOpen size={14} className="text-cyan-400" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-white tracking-tight">
                知识大脑
              </h2>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                企业知识资产 · AI 决策大脑 · 每日增长
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* 知识检索入口（Program A Phase A：所有 Runtime 第一步都是 Knowledge Search） */}
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-md border border-white/[0.06] bg-white/[0.02]">
              <Search size={11} className="text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
                placeholder="检索知识..."
                className="bg-transparent text-[11px] font-mono text-gray-300 placeholder-gray-600 outline-none w-32"
              />
              {searching ? (
                <Loader2 size={11} className="animate-spin text-cyan-400" />
              ) : (
                <button
                  onClick={handleSearch}
                  className="text-gray-500 hover:text-cyan-400 transition-colors"
                >
                  <ArrowUpRight size={11} />
                </button>
              )}
            </div>

            {/* 子区域切换 Tab */}
            <div className="flex items-center gap-1 p-0.5 rounded-md border border-white/[0.06] bg-white/[0.02]">
              {SUB_TABS.map((t) => {
                const Icon = t.icon;
                const active = subTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setSubTab(t.id)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-mono uppercase tracking-wider transition-all ${
                      active
                        ? "bg-cyan-500/15 text-cyan-300"
                        : "text-gray-500 hover:text-gray-300"
                    }`}
                  >
                    <Icon size={11} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Phase3 P0-3 业务说明（知识中心：管理员视图，业务员工默认无入口） */}
        <WorkspaceBizBar
          desc="知识大脑：Ontology 企业本体图谱，全系统 AI 决策的知识源，业务数据自动写入本体"
          accent="#22D3EE"
        />

        {/* P0 + P1 主区块导航（两组按钮：P0 已有功能 / P1 新增功能） */}
        <div className="flex items-center gap-1 px-4 py-2 border-b border-white/[0.06] bg-white/[0.01] flex-wrap">
          {/* P0 组 */}
          <div className="flex items-center gap-1">
            <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider mr-1">
              P0
            </span>
            {P0_SECTIONS.filter(
              (s) => canCopyContent() || !EMPLOYEE_HIDDEN_SECTIONS.includes(s.id)
            ).map((section) => {
              const SIcon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                    isActive
                      ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30"
                      : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
                  }`}
                >
                  <SIcon size={11} />
                  {section.label}
                </button>
              );
            })}
          </div>
          {/* 分隔符 */}
          <span className="text-gray-700 mx-1">|</span>
          {/* 进阶组 */}
          <div className="flex items-center gap-1">
            <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider mr-1">
              进阶
            </span>
            {P1_SECTIONS.map((section) => {
              const SIcon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                    isActive
                      ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                      : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
                  }`}
                >
                  <SIcon size={11} />
                  {section.label}
                </button>
              );
            })}
          </div>
          {/* 分隔符 */}
          <span className="text-gray-700 mx-1">|</span>
          {/* 企业地图组（Ontology） */}
          <div className="flex items-center gap-1">
            <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider mr-1">地图</span>
            {ONTO_SECTIONS.map((section) => {
              const SIcon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                    isActive
                      ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30"
                      : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
                  }`}
                >
                  <SIcon size={11} />
                  {section.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* 主区块内容：explorer 保留原有全部功能，其他三个渲染 P0 新组件 */}
        {activeSection === "explorer" && (
          <>
        {/* 企业知识资产面板（Program A Phase A） */}
        <KnowledgeAssetPanel
          stats={assetStats}
          loading={assetStatsLoading}
          onRefresh={loadAssetStats}
          searchResults={searchResults.length > 0 ? searchResults : null}
          onClearSearch={() => {
            setSearchResults([]);
            setSearchQuery("");
          }}
          activeAssetKey={activeAssetKey}
          onCategoryClick={(key) => {
            // 分类 key 映射到 subTab：cards→cards, ideas→ideas, domain→cards(领域知识归入cards), research→research, workflows→prompts, prompts→prompts, reviews→cards, outputs→cards
            const categoryToSubTab: Record<string, SubTab> = {
              cards: "cards",
              ideas: "ideas",
              domain: "cards",
              research: "research",
              workflows: "prompts",
              prompts: "prompts",
              reviews: "cards",
              outputs: "cards",
            };
            const targetTab = categoryToSubTab[key] || "cards";
            // 设置高亮状态（让用户看到 Tab 已被选中）
            setActiveAssetKey(key);
            // 切换到目标 subTab
            setSubTab(targetTab);
            // 对于 cards 分类，同时设置 selectedCategory 过滤
            // 匹配 VaultBridge 中卡片真实 category 字段（来自子目录名）
            if (key === "cards") {
              setSelectedCategory("all");
            } else if (key === "domain") {
              // 领域知识在 09_Knowledge/Domain/{赛道} 下，category 字段为赛道名
              // 通过匹配包含 "Domain" 或 "领域" 的分类
              setSelectedCategory("Domain");
            } else if (key === "reviews") {
              setSelectedCategory("Reviews");
            } else if (key === "outputs") {
              setSelectedCategory("Outputs");
            }
          }}
        />

        {/* 内容区 */}
        <div className="flex-1 overflow-hidden p-4">
          {loading ? (
            <div className="text-center py-12 text-[11px] font-mono text-gray-500">
              正在加载知识库...
            </div>
          ) : subTab === "cards" ? (
            <CardTreeLayout
              cards={cards}
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
              onSelectCard={setSelectedCard}
            />
          ) : subTab === "all_notes" ? (
            <AllNotesTreeLayout
              notes={allNotes}
              selectedCategory={selectedNoteCategory}
              onSelectCategory={setSelectedNoteCategory}
              onSelectNote={(note) => {
                setSelectedNote(note);
                // 懒加载笔记内容
                setNoteContent({ loading: true, content: '' });
                fetch(`/vault-api/read?path=${encodeURIComponent(note.filePath)}`)
                  .then((r) => r.json())
                  .then((result) => {
                    if (result.success && result.content) {
                      setNoteContent({ loading: false, content: result.content });
                    } else {
                      setNoteContent({ loading: false, content: '', error: '读取失败' });
                    }
                  })
                  .catch((err) => {
                    setNoteContent({ loading: false, content: '', error: String(err) });
                  });
              }}
              // 任务2：目录覆盖率统计 + 递归目录树增强
              dirCoverage={dirCoverage}
              dirCoverageLoading={dirCoverageLoading}
              onReloadCoverage={loadDirCoverage}
            />
          ) : subTab === "ideas" ? (
            <IdeaGrid ideas={ideas} onSelect={setSelectedIdea} />
          ) : subTab === "research" ? (
            <ResearchList research={research} />
          ) : subTab === "graph" ? (
            <GraphView data={graphData} loading={graphLoading} onReload={async () => {
              setGraphLoading(true);
              try {
                const resp = await RuntimeManager.query(RuntimeName.Knowledge, {
                  type: KnowledgeQuery.Graph,
                });
                if (resp?.success && resp.data) {
                  // resp.data 是 KnowledgeResult，需要再解包一层拿到 { nodes, edges }
                  const inner = resp.data as { success?: boolean; data?: any };
                  if (inner?.success && inner.data) {
                    setGraphData(inner.data);
                  } else if (inner && Array.isArray((inner as any).nodes)) {
                    setGraphData(inner);
                  }
                }
              } catch (err) {
                console.error("[KnowledgeWorkspace] Graph 查询失败:", err);
              } finally {
                setGraphLoading(false);
              }
            }} />
          ) : (
            <PromptList prompts={prompts} />
          )}
        </div>
          </>
        )}

        {/* 智能问答区块（P0-功能1） */}
        {activeSection === "qa" && (
          <div className="flex-1 overflow-hidden p-4">
            <KnowledgeQA />
          </div>
        )}

        {/* 知识推荐区块（P0-功能2） */}
        {activeSection === "recommendation" && (
          <div className="flex-1 overflow-hidden p-4">
            <KnowledgeRecommendation />
          </div>
        )}

        {/* 知识质量评估区块（P0-功能3）——v6.23 员工/游客不可见（管理职能） */}
        {activeSection === "quality" &&
          (canCopyContent() ? (
            <div className="flex-1 overflow-hidden p-4">
              <KnowledgeQualityPanel />
            </div>
          ) : (
            <div className="flex-1 overflow-hidden p-4">
              <div className="flex items-center justify-center h-full text-[11px] font-mono text-gray-600">
                质量评估为管理员专属功能，无访问权限
              </div>
            </div>
          ))}

        {/* 知识自动生产区块（P1-功能4） */}
        {activeSection === "producer" && (
          <div className="flex-1 overflow-hidden p-4">
            <KnowledgeProducer />
          </div>
        )}

        {/* 知识使用反馈闭环区块（P1-功能5） */}
        {activeSection === "feedback" && (
          <div className="flex-1 overflow-hidden p-4">
            <KnowledgeFeedback />
          </div>
        )}

        {/* 知识地图与缺口分析区块（P1-功能6） */}
        {activeSection === "map" && (
          <div className="flex-1 overflow-hidden p-4">
            <KnowledgeMap />
          </div>
        )}

        {/* 知识版本与协作区块（P1-功能7） */}
        {activeSection === "version" && (
          <div className="flex-1 overflow-hidden p-4">
            <KnowledgeVersion />
          </div>
        )}

        {/* 知识演化区块（第十一个 Runtime，第十五章 Knowledge Evolution Protocol） */}
        {activeSection === "evolution" && (
          <div className="flex-1 overflow-y-auto p-4">
            <KnowledgeEvolutionPanel />
          </div>
        )}

        {/* P2-功能8：智能培训系统 */}
        {activeSection === "training" && (
          <div className="flex-1 overflow-y-auto p-4">
            <SmartTrainingSystem />
          </div>
        )}

        {/* P2-功能9：专家网络与问答社区 */}
        {activeSection === "expertNetwork" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ExpertNetwork />
          </div>
        )}

        {/* P2-功能10：知识商业化 */}
        {activeSection === "monetization" && (
          <div className="flex-1 overflow-y-auto p-4">
            <KnowledgeMonetization />
          </div>
        )}

        {/* v5.2 审计修复 P1：知识飞轮入口（软件操作→执行经验→知识自动沉淀，对应白皮书第十五章） */}
        {activeSection === "flywheel" && (
          <div className="flex-1 overflow-y-auto p-4">
            <KnowledgeFlywheelEntry />
          </div>
        )}
        {/* P0① 差距补齐：Ontology 企业地图 */}
        {activeSection === "ontology" && (
          <div className="flex-1 overflow-y-auto p-4">
            <OntologyPanel />
          </div>
        )}

        {/* 卡片详情预览面板 */}
        {selectedCard && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSelectedCard(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-2xl mx-4 rounded-xl border border-cyan-500/30 bg-[#0a0e1a]/95 backdrop-blur-xl p-6 max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-mono text-cyan-400 uppercase tracking-wider">{selectedCard.category}</span>
                <button onClick={() => setSelectedCard(null)} className="text-gray-500 hover:text-white"><X size={16} /></button>
              </div>
              <h2 className="text-lg font-semibold text-white mb-3">{selectedCard.title}</h2>
              {selectedCard.filePath && (
                <div className="text-[10px] font-mono text-gray-600 mb-3 break-all">📁 {selectedCard.filePath}</div>
              )}
              <div className="text-[12px] text-gray-300 leading-relaxed whitespace-pre-wrap">
                {selectedCard.content || selectedCard.summary || "暂无详细内容"}
              </div>
              {selectedCard.tags && selectedCard.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-4">
                  {selectedCard.tags.map((tag, i) => (
                    <span key={i} className="text-[9px] font-mono px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400">#{tag}</span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/[0.06]">
                <span className="text-[9px] font-mono text-gray-600">{selectedCard.date}</span>
                {selectedCard.verified && <span className="text-[9px] font-mono text-emerald-400">✓ 已验证</span>}
              </div>
            </motion.div>
          </div>
        )}

        {/* 灵感详情预览面板 */}
        {selectedIdea && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSelectedIdea(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-2xl mx-4 rounded-xl border border-amber-500/30 bg-[#0a0e1a]/95 backdrop-blur-xl p-6 max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Lightbulb size={14} className="text-amber-400" />
                  <span className="text-[10px] font-mono text-amber-400 uppercase tracking-wider">{selectedIdea.category}</span>
                </div>
                <button onClick={() => setSelectedIdea(null)} className="text-gray-500 hover:text-white"><X size={16} /></button>
              </div>
              <h2 className="text-lg font-semibold text-white mb-3">{selectedIdea.title}</h2>
              {selectedIdea.filePath && (
                <div className="text-[10px] font-mono text-gray-600 mb-3 break-all">📁 {selectedIdea.filePath}</div>
              )}
              <div className="text-[12px] text-gray-300 leading-relaxed whitespace-pre-wrap">
                {selectedIdea.content || selectedIdea.summary || "暂无详细内容"}
              </div>
              {selectedIdea.tags && selectedIdea.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-4">
                  {selectedIdea.tags.map((tag, i) => (
                    <span key={i} className="text-[9px] font-mono px-2 py-0.5 rounded bg-amber-500/10 text-amber-400">#{tag}</span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/[0.06]">
                <span className="text-[9px] font-mono text-gray-600">{selectedIdea.date}</span>
              </div>
            </motion.div>
          </div>
        )}

        {/* 全部笔记详情预览面板 */}
        {selectedNote && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => { setSelectedNote(null); setNoteContent(null); }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-2xl mx-4 rounded-xl border border-cyan-500/30 bg-[#0a0e1a]/95 backdrop-blur-xl p-6 max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Files size={14} className="text-cyan-400 flex-shrink-0" />
                  <span className="text-[10px] font-mono text-cyan-400 uppercase tracking-wider truncate">{selectedNote.category}</span>
                </div>
                <button onClick={() => { setSelectedNote(null); setNoteContent(null); }} className="text-gray-500 hover:text-white flex-shrink-0"><X size={16} /></button>
              </div>
              <h2 className="text-lg font-semibold text-white mb-3">{selectedNote.title}</h2>
              <div className="text-[10px] font-mono text-gray-600 mb-3 break-all">📁 {selectedNote.filePath}</div>
              <div className="text-[12px] text-gray-300 leading-relaxed whitespace-pre-wrap">
                {noteContent?.loading ? (
                  <span className="flex items-center gap-2 text-gray-500">
                    <Loader2 size={12} className="animate-spin" /> 加载中...
                  </span>
                ) : noteContent?.error ? (
                  <span className="text-red-400">{noteContent.error}</span>
                ) : noteContent?.content ? (
                  noteContent.content
                ) : (
                  "暂无内容"
                )}
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/[0.06]">
                <span className="text-[9px] font-mono text-gray-600">
                  修改时间: {selectedNote.mtime ? new Date(selectedNote.mtime).toLocaleString("zh-CN") : "未知"} · 大小: {(selectedNote.size / 1024).toFixed(1)}KB
                </span>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

// ============================================
// 子组件：Card 网格（保留兼容）
// ============================================

function CardGrid({ cards, onSelect }: { cards: KnowledgeCard[]; onSelect?: (card: KnowledgeCard) => void }) {
  if (cards.length === 0) {
    return <EmptyState message="暂无 Card 数据" />;
  }
  return (
    <div className="grid grid-cols-3 gap-3">
      {cards.map((c, idx) => (
        <motion.div
          key={c.id || idx}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: idx * 0.03 }}
          onClick={() => onSelect?.(c)}
          className="relative rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 hover:border-cyan-500/30 hover:bg-white/[0.04] transition-colors group cursor-pointer"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-mono text-cyan-400 uppercase tracking-wider">
              {c.category}
            </span>
            <FileText size={11} className="text-gray-600" />
          </div>
          <h3 className="text-[13px] font-medium text-white mb-1 line-clamp-2">
            {c.title}
          </h3>
          <p className="text-[11px] text-gray-400 line-clamp-3 leading-snug">
            {c.summary}
          </p>
          <div className="mt-2 flex items-center justify-between text-[9px] font-mono text-gray-600">
            <span>{c.date}</span>
            <ArrowUpRight
              size={11}
              className="opacity-0 group-hover:opacity-100 text-cyan-400 transition-opacity"
            />
          </div>
        </motion.div>
      ))}
    </div>
  );
}

// ============================================
// 子组件：Card 目录树布局（左侧分类 + 右侧卡片）
// 按 Obsidian Vault 真实目录结构分类
// ============================================

function CardTreeLayout({
  cards,
  selectedCategory,
  onSelectCategory,
  onSelectCard,
}: {
  cards: KnowledgeCard[];
  selectedCategory: string;
  onSelectCategory: (cat: string) => void;
  onSelectCard?: (card: KnowledgeCard) => void;
}) {
  // 按 category 分组统计
  const categories = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of cards) {
      const cat = c.category || "未分类";
      map.set(cat, (map.get(cat) || 0) + 1);
    }
    // 按数量倒序
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [cards]);

  // 过滤后的卡片
  const filteredCards = useMemo(() => {
    if (selectedCategory === "all") return cards;
    return cards.filter((c) => (c.category || "未分类") === selectedCategory);
  }, [cards, selectedCategory]);

  return (
    <div className="h-full flex gap-3 overflow-hidden">
      {/* 左侧目录树 */}
      <div className="w-48 flex-shrink-0 flex flex-col rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden">
        <div className="px-3 py-2 border-b border-white/[0.06]">
          <div className="flex items-center gap-1.5">
            <Folder size={12} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
              Vault 目录
            </span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-1">
          {/* 全部 */}
          <button
            onClick={() => onSelectCategory("all")}
            className={`w-full text-left px-3 py-1.5 flex items-center justify-between hover:bg-white/[0.03] transition-colors ${
              selectedCategory === "all" ? "bg-cyan-500/10" : ""
            }`}
          >
            <span className={`text-[11px] font-mono ${selectedCategory === "all" ? "text-cyan-300" : "text-gray-400"}`}>
              全部
            </span>
            <span className="text-[9px] font-mono text-gray-600">{cards.length}</span>
          </button>
          {categories.map(([cat, count]) => (
            <button
              key={cat}
              onClick={() => onSelectCategory(cat)}
              className={`w-full text-left px-3 py-1.5 flex items-center justify-between hover:bg-white/[0.03] transition-colors group ${
                selectedCategory === cat ? "bg-cyan-500/10" : ""
              }`}
            >
              <div className="flex items-center gap-1 min-w-0">
                <ChevronRight
                  size={9}
                  className={`text-gray-600 transition-transform ${selectedCategory === cat ? "rotate-90 text-cyan-400" : ""}`}
                />
                <span className={`text-[11px] truncate ${selectedCategory === cat ? "text-cyan-300" : "text-gray-400"}`}>
                  {cat}
                </span>
              </div>
              <span className="text-[9px] font-mono text-gray-600 ml-1">{count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 右侧卡片网格 */}
      <div className="flex-1 min-w-0 overflow-y-auto">
        <div className="mb-2 px-1 flex items-center justify-between">
          <span className="text-[10px] font-mono text-gray-500">
            {selectedCategory === "all" ? "全部卡片" : `分类: ${selectedCategory}`} · {filteredCards.length} 张
          </span>
        </div>
        <CardGrid cards={filteredCards} onSelect={onSelectCard} />
      </div>
    </div>
  );
}

// ============================================
// 子组件：全部笔记目录树布局（左侧顶级目录 + 右侧笔记列表）
// 覆盖整个 Obsidian Vault，不限于 09_Knowledge/Cards
// ============================================

function AllNotesTreeLayout({
  notes,
  selectedCategory,
  onSelectCategory,
  onSelectNote,
  dirCoverage,
  dirCoverageLoading,
  onReloadCoverage,
}: {
  notes: VaultNoteItem[];
  selectedCategory: string;
  onSelectCategory: (cat: string) => void;
  onSelectNote?: (note: VaultNoteItem) => void;
  /** 任务2：Vault 顶层目录覆盖率统计（来自 /vault-api/list） */
  dirCoverage: DirCoverageItem[];
  /** 目录覆盖率加载中标记 */
  dirCoverageLoading: boolean;
  /** 重新加载目录覆盖率 */
  onReloadCoverage: () => void;
}) {
  // 已展开的目录 path 集合（默认展开第一个顶层目录，便于用户立即看到子目录）
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  /**
   * 从 notes 的 filePath 构建递归目录树
   * filePath 形如 "09_Knowledge（知识中心）/Cards/Domain/xxx.md"
   * 按层级切分后构建 TreeNode 递归结构
   */
  const treeRoot = useMemo<TreeNode>(() => {
    const root: TreeNode = {
      name: 'Vault Root',
      path: '',
      isDirectory: true,
      children: [],
      notes: [],
      fileCount: 0,
      totalFileCount: 0,
    };
    // 用 Map 缓存已创建的目录节点，避免重复
    const dirMap = new Map<string, TreeNode>();
    dirMap.set('', root);

    /** 获取或创建指定 path 的目录节点（连同所有祖先目录一起创建） */
    const getOrCreateDir = (dirPath: string): TreeNode => {
      if (dirMap.has(dirPath)) return dirMap.get(dirPath)!;
      const parts = dirPath.split('/');
      const name = parts[parts.length - 1];
      const parentPath = parts.slice(0, -1).join('/');
      const parent = getOrCreateDir(parentPath);
      const node: TreeNode = {
        name,
        path: dirPath,
        isDirectory: true,
        children: [],
        notes: [],
        fileCount: 0,
        totalFileCount: 0,
      };
      parent.children.push(node);
      dirMap.set(dirPath, node);
      return node;
    };

    // 遍历所有笔记，按 filePath 归类到对应目录节点
    for (const note of notes) {
      const fp = note.filePath || '';
      if (!fp) {
        root.notes.push(note);
        continue;
      }
      const parts = fp.split('/');
      // 文件所在目录路径（去掉最后一段文件名）
      const dirPath = parts.slice(0, -1).join('/');
      const dirNode = getOrCreateDir(dirPath);
      dirNode.notes.push(note);
      dirNode.fileCount++;
    }

    /** 递归计算每个目录节点的 totalFileCount（含所有子目录） */
    const computeTotal = (node: TreeNode): number => {
      let total = node.fileCount;
      for (const child of node.children) {
        total += computeTotal(child);
      }
      node.totalFileCount = total;
      return total;
    };
    computeTotal(root);

    // 子目录按名称排序（数字前缀优先）
    const sortRecursive = (node: TreeNode) => {
      node.children.sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
      node.children.forEach(sortRecursive);
    };
    sortRecursive(root);

    return root;
  }, [notes]);

  // 顶层目录列表（用于覆盖率面板对照显示）
  const topDirsFromNotes = useMemo(() => treeRoot.children, [treeRoot]);

  // 过滤后的笔记：基于 selectedCategory（即选中的目录 path）做前缀匹配
  const filteredNotes = useMemo(() => {
    if (selectedCategory === "all") return notes;
    // 选中某个目录时，展示该目录及其所有子目录下的笔记
    const prefix = selectedCategory + '/';
    return notes.filter((n) => (n.filePath || '').startsWith(prefix));
  }, [notes, selectedCategory]);

  /** 切换目录展开/收起 */
  const toggleExpand = useCallback((path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  /** 选中目录：同时展开该目录 */
  const handleSelectDir = useCallback((path: string) => {
    onSelectCategory(path);
    if (path) {
      setExpandedPaths((prev) => {
        if (prev.has(path)) return prev;
        const next = new Set(prev);
        next.add(path);
        return next;
      });
    }
  }, [onSelectCategory]);

  // 覆盖率统计汇总
  const coverageStats = useMemo(() => {
    const totalDirs = dirCoverage.length;
    const nonEmptyDirs = dirCoverage.filter((d) => d.totalFileCount > 0).length;
    const totalFiles = dirCoverage.reduce((sum, d) => sum + d.totalFileCount, 0);
    const coverageRate = totalDirs > 0 ? Math.round((nonEmptyDirs / totalDirs) * 100) : 0;
    return { totalDirs, nonEmptyDirs, totalFiles, coverageRate };
  }, [dirCoverage]);

  return (
    <div className="h-full flex flex-col gap-2 overflow-hidden">
      {/* 任务2：目录覆盖率统计面板（顶部横向 banner） */}
      <div className="flex-shrink-0 rounded-lg border border-cyan-500/20 bg-cyan-500/[0.02] px-3 py-2">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <Folder size={11} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-cyan-300 uppercase tracking-wider">
              Obsidian Vault 目录覆盖率
            </span>
            {/* 覆盖率汇总 */}
            {!dirCoverageLoading && dirCoverage.length > 0 && (
              <span className="text-[9px] font-mono text-gray-500 ml-1">
                · {coverageStats.nonEmptyDirs}/{coverageStats.totalDirs} 目录有内容 · 覆盖率 {coverageStats.coverageRate}% · 共 {coverageStats.totalFiles} 篇
              </span>
            )}
          </div>
          <button
            onClick={onReloadCoverage}
            disabled={dirCoverageLoading}
            className="flex items-center gap-1 text-[9px] font-mono text-gray-500 hover:text-cyan-400 transition-colors disabled:opacity-50"
            title="重新扫描 Vault 目录覆盖率"
          >
            {dirCoverageLoading ? (
              <Loader2 size={9} className="animate-spin" />
            ) : (
              <RefreshCw size={9} />
            )}
            刷新
          </button>
        </div>
        {/* 各顶层目录文件数列表（横向 chips，点击可切换到该目录） */}
        {dirCoverageLoading && dirCoverage.length === 0 ? (
          <div className="text-[10px] font-mono text-gray-500 py-1">正在扫描 Vault 目录...</div>
        ) : dirCoverage.length === 0 ? (
          // 白皮书铁律一：无数据显示"暂无数据"
          <div className="text-[10px] font-mono text-gray-500 py-1">暂无数据，知识库暂未接入</div>
        ) : (
          <div className="flex flex-wrap gap-1">
            {dirCoverage.map((d) => {
              const active = selectedCategory === d.path;
              const isEmpty = d.totalFileCount === 0;
              // v5.2 审计修复 P2-⑤：只读区与热区分离（白皮书第十章：内核/理论/战略层只读）
              const READONLY_PREFIXES = ["00_", "09_Knowledge", "11_Strategy", "12_Theory", "13_SOP", "14_Brand", "99_"];
              const isReadOnly = READONLY_PREFIXES.some((p) => d.name?.startsWith(p));
              return (
                <button
                  key={d.path}
                  onClick={() => handleSelectDir(d.path)}
                  title={`${d.path} · 直接 ${d.fileCount} 篇 · 含子目录共 ${d.totalFileCount} 篇${isReadOnly ? " · 只读区（内核/理论/战略层，仅系统写回）" : ""}`}
                  className={`px-2 py-0.5 rounded-md border text-[9px] font-mono transition-colors cursor-pointer ${
                    active
                      ? "border-cyan-500/40 bg-cyan-500/15 text-cyan-200"
                      : isEmpty
                      ? "border-white/[0.04] bg-white/[0.01] text-gray-600 hover:bg-white/[0.03]"
                      : "border-white/[0.06] bg-white/[0.02] text-gray-400 hover:border-cyan-500/30 hover:bg-cyan-500/[0.05]"
                  }`}
                >
                  <span className="truncate max-w-[130px] inline-block align-bottom">{d.name}</span>
                  {isReadOnly && (
                    <span className="ml-1 px-0.5 rounded bg-amber-500/10 text-amber-400/90 border border-amber-500/20 text-[8px]">
                      只读
                    </span>
                  )}
                  <span className={`ml-1 ${isEmpty ? 'text-gray-700' : active ? 'text-cyan-200' : 'text-gray-300'}`}>
                    {d.totalFileCount}
                  </span>
                  {isEmpty && <span className="ml-1 text-gray-700">∅</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* 下方：左侧递归目录树 + 右侧笔记列表 */}
      <div className="flex-1 min-h-0 flex gap-3 overflow-hidden">
        {/* 左侧递归目录树 */}
        <div className="w-64 flex-shrink-0 flex flex-col rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden">
          <div className="px-3 py-2 border-b border-white/[0.06]">
            <div className="flex items-center gap-1.5">
              <Folder size={12} className="text-cyan-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                Vault 全部目录（递归）
              </span>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto py-1">
            {/* 全部笔记 */}
            <button
              onClick={() => onSelectCategory("all")}
              className={`w-full text-left px-3 py-1.5 flex items-center justify-between hover:bg-white/[0.03] transition-colors ${
                selectedCategory === "all" ? "bg-cyan-500/10" : ""
              }`}
            >
              <span className={`text-[11px] font-mono ${selectedCategory === "all" ? "text-cyan-300" : "text-gray-400"}`}>
                全部
              </span>
              <FileCountBadge count={notes.length} />
            </button>
            {/* 递归渲染目录树 */}
            {topDirsFromNotes.map((node) => (
              <RecursiveTreeNode
                key={node.path}
                node={node}
                depth={0}
                expandedPaths={expandedPaths}
                selectedCategory={selectedCategory}
                onToggleExpand={toggleExpand}
                onSelectDir={handleSelectDir}
              />
            ))}
          </div>
        </div>

        {/* 右侧笔记列表 */}
        <div className="flex-1 min-w-0 overflow-y-auto">
          <div className="mb-2 px-1 flex items-center justify-between">
            <span className="text-[10px] font-mono text-gray-500">
              {selectedCategory === "all" ? "全部笔记" : `目录: ${selectedCategory}`} · {filteredNotes.length} 篇
            </span>
          </div>
          {filteredNotes.length === 0 ? (
            <EmptyState message="暂无笔记，知识库暂未接入" />
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {filteredNotes.map((note, idx) => (
                <motion.div
                  key={`${note.filePath}-${idx}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: Math.min(idx * 0.02, 0.4) }}
                  onClick={() => onSelectNote?.(note)}
                  className="relative rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5 hover:border-cyan-500/30 hover:bg-white/[0.04] transition-colors group cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[9px] font-mono text-cyan-400/80 uppercase tracking-wider truncate">
                      {note.category}
                    </span>
                    <FileText size={10} className="text-gray-600 flex-shrink-0" />
                  </div>
                  <h3 className="text-[12px] font-medium text-white mb-1 line-clamp-2 break-all">
                    {note.title}
                  </h3>
                  <div className="text-[9px] font-mono text-gray-600 truncate" title={note.filePath}>
                    {note.filePath}
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-[8px] font-mono text-gray-700">
                    <span>{note.mtime ? new Date(note.mtime).toLocaleDateString("zh-CN") : ""}</span>
                    <span>{(note.size / 1024).toFixed(1)}KB</span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * 递归目录树节点组件
 *
 * - 显示目录名 + 文件数徽章
 * - 点击目录名选中该目录（过滤右侧笔记）
 * - 点击展开箭头递归展开/收起子目录
 */
function RecursiveTreeNode({
  node,
  depth,
  expandedPaths,
  selectedCategory,
  onToggleExpand,
  onSelectDir,
}: {
  node: TreeNode;
  depth: number;
  expandedPaths: Set<string>;
  selectedCategory: string;
  onToggleExpand: (path: string) => void;
  onSelectDir: (path: string) => void;
}) {
  const isExpanded = expandedPaths.has(node.path);
  const isSelected = selectedCategory === node.path;
  const hasChildren = node.children.length > 0;
  // 缩进：每层 12px
  const paddingLeft = 12 + depth * 12;

  return (
    <div>
      <div
        className={`w-full text-left py-1.5 flex items-center justify-between hover:bg-white/[0.03] transition-colors group ${
          isSelected ? "bg-cyan-500/10" : ""
        }`}
        style={{ paddingLeft, paddingRight: 12 }}
      >
        <div className="flex items-center gap-1 min-w-0">
          {/* 展开/收起箭头（无子目录时占位） */}
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(node.path);
              }}
              className="flex-shrink-0 text-gray-600 hover:text-cyan-400 transition-colors"
              title={isExpanded ? "收起" : "展开"}
            >
              {isExpanded ? <ChevronDown size={9} /> : <ChevronRight size={9} />}
            </button>
          ) : (
            <span className="w-[9px] flex-shrink-0" />
          )}
          <button
            onClick={() => onSelectDir(node.path)}
            className={`flex items-center gap-1 min-w-0 truncate ${
              isSelected ? "text-cyan-300" : "text-gray-400"
            }`}
            title={node.path}
          >
            <Folder size={10} className={isSelected ? "text-cyan-400" : "text-gray-600"} />
            <span className="text-[11px] truncate">{node.name}</span>
          </button>
        </div>
        {/* 文件数徽章：显示该目录递归文件总数 */}
        <FileCountBadge count={node.totalFileCount} direct={node.fileCount} />
      </div>
      {/* 递归渲染子目录 */}
      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <RecursiveTreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              expandedPaths={expandedPaths}
              selectedCategory={selectedCategory}
              onToggleExpand={onToggleExpand}
              onSelectDir={onSelectDir}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** 文件数徽章：默认显示递归总数，hover title 显示直接文件数 */
function FileCountBadge({ count, direct }: { count: number; direct?: number }) {
  const title = typeof direct === 'number' && direct !== count
    ? `直接 ${direct} 篇 · 含子目录共 ${count} 篇`
    : `${count} 篇`;
  return (
    <span
      className="text-[9px] font-mono text-gray-600 ml-1 px-1 py-0.5 rounded bg-white/[0.03]"
      title={title}
    >
      {count}
    </span>
  );
}

// ============================================
// 子组件：Graph 关系图谱（力导向 Canvas，类 Obsidian Graph）
// 支持三种布局：力导向 / 球形 / 网格（G 键或按钮切换）
// 交互：拖拽节点 · 悬停高亮 · 点击查看详情
// ============================================

function GraphView({
  data,
  loading,
  onReload,
}: {
  data: any;
  loading: boolean;
  onReload: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [layoutMode, setLayoutMode] = useState<"force" | "sphere" | "grid">("force");
  // 孤岛治理：隐藏仅有目录归属、无任何关联的孤立笔记节点
  // v6.1.1 默认开启：用户反馈"图谱全是孤岛、乱"——默认只显示有关联的节点，呈现清晰网络
  const [hideIslands, setHideIslands] = useState(true);
  const hideIslandsRef = useRef(true);
  useEffect(() => {
    hideIslandsRef.current = hideIslands;
  }, [hideIslands]);

  const rawNodes: any[] = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data.nodes)) return data.nodes;
    if (Array.isArray(data)) return data;
    return [];
  }, [data]);

  const rawEdges: any[] = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data.edges)) return data.edges;
    if (Array.isArray(data.links)) return data.links;
    return [];
  }, [data]);

  // 孤岛计数（note 节点无任何 link/tag 关联边）
  const islandCount = useMemo(() => {
    if (rawNodes.length === 0) return 0;
    const relMap: Record<string, Set<string>> = {};
    rawEdges.forEach((e) => {
      const s = String(e.from || e.source);
      const t = String(e.to || e.target);
      const rel = e.relation || "link";
      if (rel === "parent") return;
      (relMap[s] ||= new Set()).add(t);
      (relMap[t] ||= new Set()).add(s);
    });
    return rawNodes.filter(
      (n) => String(n.type || "note") === "note" && !(relMap[String(n.id)]?.size)
    ).length;
  }, [rawNodes, rawEdges]);

  // 力导向模拟状态
  const simRef = useRef<{
    nodes: { id: string; label: string; type?: string; x: number; y: number; vx: number; vy: number; color: string; fx?: number; fy?: number; degree: number; linkCount?: number; size?: number }[];
    edges: { source: string; target: string; relation: string }[];
    width: number; height: number; alpha: number;
  }>({ nodes: [], edges: [], width: 800, height: 600, alpha: 1 });

  // 初始化节点位置
  useEffect(() => {
    if (rawNodes.length === 0) return;
    const w = containerRef.current?.clientWidth || 800;
    const h = containerRef.current?.clientHeight || 600;
    const colors = ["#A78BFA", "#38BDF8", "#34D399", "#FBBF24", "#F87171", "#60A5FA", "#EC4899"];
    const typeColor: Record<string, string> = {
      note: "#A78BFA",
      folder: "#38BDF8",
      tag: "#34D399",
    };
    const nodes = rawNodes.map((n, i) => {
      const angle = (i * 2 * Math.PI) / rawNodes.length;
      const r = Math.min(w, h) * 0.3;
      const nodeType = String(n.type || 'note');
      return {
        id: String(n.id || n.title || `node-${i}`),
        label: String(n.label || n.title || n.id || `Node ${i}`),
        type: nodeType,
        // 保留原始节点字段（用于详情面板关联文章）
        raw: n,
        x: w / 2 + r * Math.cos(angle) + (Math.random() - 0.5) * 50,
        y: h / 2 + r * Math.sin(angle) + (Math.random() - 0.5) * 50,
        vx: 0, vy: 0,
        color: n.color || typeColor[nodeType] || colors[i % colors.length],
        degree: 0,
        linkCount: 0,
        // 节点权重（server 端计算）：folder/tag/聚合=3（图谱 index），note 按关联度 1-2
        size: Number((n as any).size) || (nodeType === "folder" || nodeType === "tag" ? 3 : 1),
      };
    });
    const nodeMap = new Map(nodes.map((n) => [n.id, n]));
    // 保留 relation 字段，便于关联节点展示
    const edges = rawEdges.map((e) => ({ source: String(e.from || e.source), target: String(e.to || e.target), relation: e.relation || 'link' })).filter((e) => nodeMap.has(e.source) && nodeMap.has(e.target));
    // 计算每个节点的连接度，用于决定是否常显标签；linkCount 用于孤岛判定
    nodes.forEach((n) => { n.degree = 0; n.linkCount = 0; });
    edges.forEach((e) => {
      const s = nodeMap.get(e.source); if (s) s.degree = (s.degree || 0) + 1;
      const t = nodeMap.get(e.target); if (t) t.degree = (t.degree || 0) + 1;
      // 非 parent 关系（link/tag）才计入 linkCount
      if (e.relation !== "parent") {
        if (s) s.linkCount = (s.linkCount || 0) + 1;
        if (t) t.linkCount = (t.linkCount || 0) + 1;
      }
    });
    simRef.current = { nodes, edges, width: w, height: h, alpha: 1 };
  }, [rawNodes, rawEdges]);

  // 用 ref 同步选中节点到渲染循环（避免 useEffect 重运行）
  const selectedNodeRef = useRef<any>(null);
  useEffect(() => {
    selectedNodeRef.current = selectedNode;
  }, [selectedNode]);

  // 选中节点的关联节点（基于 edges 计算邻接节点）
  const adjacentNodes = useMemo(() => {
    if (!selectedNode) return [];
    const sim = simRef.current;
    const result: Array<{ node: any; relation: string }> = [];
    for (const e of sim.edges) {
      if (e.source === selectedNode.id) {
        const target = sim.nodes.find((n) => n.id === e.target);
        if (target) result.push({ node: target, relation: (e as any).relation || 'link' });
      } else if (e.target === selectedNode.id) {
        const source = sim.nodes.find((n) => n.id === e.source);
        if (source) result.push({ node: source, relation: (e as any).relation || 'link' });
      }
    }
    return result.slice(0, 8); // 最多显示 8 个关联节点
  }, [selectedNode]);

  // 加载选中节点对应的文章内容
  const [nodeContent, setNodeContent] = useState<{ loading: boolean; content: string; error?: string; path?: string } | null>(null);
  useEffect(() => {
    if (!selectedNode) {
      setNodeContent(null);
      return;
    }
    // 对所有 note 类型节点都尝试加载文章内容
    // VaultBridge.buildGraph 返回的 note 节点 id 是文件相对路径（如 09_Knowledge（知识中心）/Cards/xxx.md）
    // 占位节点 id 以 note: 开头（如 note:xxx），也尝试用关键词搜索
    const isNote = selectedNode.type === 'note';
    if (!isNote) {
      setNodeContent(null);
      return;
    }
    let cancelled = false;
    setNodeContent({ loading: true, content: '', path: selectedNode.id });
    (async () => {
      try {
        // 如果 id 包含 '/' 且不以 note: 开头，直接读取文件
        if (selectedNode.id.includes('/') && !selectedNode.id.startsWith('note:')) {
          const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(selectedNode.id)}`);
          if (cancelled) return;
          if (resp.ok) {
            const result = await resp.json();
            if (result.success && result.content) {
              const preview = result.content.length > 600
                ? result.content.slice(0, 600) + '\n\n... (完整内容请点击下方按钮)'
                : result.content;
              setNodeContent({ loading: false, content: preview, path: selectedNode.id });
              return;
            }
          }
          // 直接读取失败，尝试通过 Knowledge Search 查找
        }
        // 对于占位节点或直接读取失败的情况，通过 Knowledge Search 搜索匹配文件
        const searchKeyword = selectedNode.id.startsWith('note:')
          ? selectedNode.id.slice(5) // 去掉 'note:' 前缀
          : selectedNode.label || selectedNode.id;
        try {
          const searchResp = await RuntimeManager.query(RuntimeName.Knowledge, {
            type: KnowledgeQuery.Search,
            options: { keyword: searchKeyword, limit: 5 },
          });
          if (cancelled) return;
          const inner = searchResp?.data as any;
          const results = inner?.success ? (inner.data || []) : (Array.isArray(inner) ? inner : []);
          if (Array.isArray(results) && results.length > 0) {
            // 找到匹配文件，读取第一个
            const firstMatch = results[0];
            const matchedPath = firstMatch.path || firstMatch.fileName || firstMatch.id;
            if (matchedPath && matchedPath.includes('/')) {
              const readResp = await fetch(`/vault-api/read?path=${encodeURIComponent(matchedPath)}`);
              if (cancelled) return;
              if (readResp.ok) {
                const readResult = await readResp.json();
                if (readResult.success && readResult.content) {
                  const preview = readResult.content.length > 600
                    ? readResult.content.slice(0, 600) + '\n\n... (完整内容请点击下方按钮)'
                    : readResult.content;
                  setNodeContent({ loading: false, content: preview, path: matchedPath });
                  return;
                }
              }
            }
            // 搜索到了但无法读取，显示搜索结果摘要
            const summary = firstMatch.summary || firstMatch.content?.slice(0, 400) || `找到 ${results.length} 个匹配文件，但无法读取内容`;
            setNodeContent({ loading: false, content: summary, path: matchedPath });
            return;
          }
        } catch {
          // 搜索也失败
        }
        setNodeContent({ loading: false, content: '', error: '未找到关联文章文件' });
      } catch (err: any) {
        if (!cancelled) setNodeContent({ loading: false, content: '', error: err?.message || String(err) });
      }
    })();
    return () => { cancelled = true; };
  }, [selectedNode]);

  // 在新 Modal 中打开完整文章
  const [articleModal, setArticleModal] = useState<{ title: string; content: string; loading: boolean } | null>(null);
  const openFullArticle = async (node: any) => {
    if (!node?.id) return;
    setArticleModal({ title: node.label, content: '', loading: true });
    try {
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(node.id)}`);
      if (resp.ok) {
        const result = await resp.json();
        setArticleModal({ title: node.label, content: result.content || '暂无内容', loading: false });
      } else {
        setArticleModal({ title: node.label, content: `加载失败：HTTP ${resp.status}`, loading: false });
      }
    } catch (err: any) {
      setArticleModal({ title: node.label, content: `加载失败：${err?.message || err}`, loading: false });
    }
  };

  // 渲染循环 + 力导向
  useEffect(() => {
    if (rawNodes.length === 0) return;
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d"); if (!ctx) return;
    let raf = 0;
    // dragging 增加 startX/startY 用于计算累积移动距离，避免轻微移动被误判为拖拽
    let dragging: { node: any; offsetX: number; offsetY: number; moved: boolean; startX: number; startY: number } | null = null;
    const hoverRef = { current: null as any };
    // selectedNodeRef 已提升到组件作用域（useRef），此处直接使用

    const resize = () => {
      const c = containerRef.current; if (!c) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = c.clientWidth * dpr; canvas.height = c.clientHeight * dpr;
      canvas.style.width = `${c.clientWidth}px`; canvas.style.height = `${c.clientHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      simRef.current.width = c.clientWidth; simRef.current.height = c.clientHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    if (containerRef.current) ro.observe(containerRef.current);

    const tick = () => {
      const sim = simRef.current;
      const { nodes, edges, width, height } = sim;
      if (nodes.length > 0) {
        if (layoutMode === "sphere") {
          // 分层球体：核心 index（目录/标签/聚合节点，size=3）居中大球，
          // 中关联（size=2）中环，普通 note（size=1）外球面 —— 避免大数据量节点占满球面
          const num = Math.max(nodes.length, 1);
          const R = Math.min(width, height) * 0.42;
          const goldenAngle = 2.399963229728653; // 黄金角 (360° * (1 - 1/φ))
          const rot = (Date.now() / 6000) % (Math.PI * 2); // 缓慢自转
          const coreNodes = nodes.filter((n) => (n.size || 1) >= 3);
          const midNodes = nodes.filter((n) => (n.size || 1) === 2);
          const shellNodes = nodes.filter((n) => (n.size || 1) < 2);
          const placeSphere = (list: any[], radius: number) => {
            const cnt = Math.max(list.length, 1);
            list.forEach((n, i) => {
              if (n.fx != null) return;
              // 3D 球面坐标（斐波那契均匀分布，每层独立计数）
              const th = goldenAngle * i;
              const ph = Math.acos(1 - 2 * (i + 0.5) / cnt);
              let x = radius * Math.sin(ph) * Math.cos(th);
              let z = radius * Math.sin(ph) * Math.sin(th);
              const y = radius * Math.cos(ph);
              // 绕 Y 轴旋转
              const rx = x * Math.cos(rot) - z * Math.sin(rot);
              const rz = x * Math.sin(rot) + z * Math.cos(rot);
              // 透视投影：z 越大（越近）缩放越大，形成球体纵深感
              const persp = 1 + (rz / R) * 0.25;
              n.x = width / 2 + rx * persp;
              n.y = height / 2 + y * persp;
              (n as any).z = rz; // 保存深度用于渲染
            });
          };
          placeSphere(shellNodes, R);        // 普通笔记：外球面（小球）
          placeSphere(midNodes, R * 0.55);   // 中关联：中环
          placeSphere(coreNodes, R * 0.18);  // 核心 index：近中心（大球）
        } else if (layoutMode === "grid") {
          const cols = Math.ceil(Math.sqrt(nodes.length));
          const cw = width / (cols + 1), ch = height / (Math.ceil(nodes.length / cols) + 1);
          nodes.forEach((n, i) => { if (n.fx != null) return; n.x = (i % cols + 1) * cw; n.y = (Math.floor(i / cols) + 1) * ch; });
        } else {
          // 力导向
          const cx = width / 2, cy = height / 2;
          // 斥力常数按节点数缩放，节点越多斥力越小，避免过度分散
          const repulsion = 300 / Math.max(nodes.length, 20);
          for (let i = 0; i < nodes.length; i++) for (let j = i + 1; j < nodes.length; j++) {
            const a = nodes[i], b = nodes[j];
            let dx = b.x - a.x, dy = b.y - a.y, d = Math.sqrt(dx * dx + dy * dy);
            if (d < 1) { d = 1; dx = Math.random(); dy = Math.random(); }
            const f = repulsion / (d * d), fx = (dx / d) * f, fy = (dy / d) * f;
            if (a.fx == null) { a.vx -= fx; a.vy -= fy; }
            if (b.fx == null) { b.vx += fx; b.vy += fy; }
          }
          const nm = new Map(nodes.map((n) => [n.id, n]));
          for (const e of edges) {
            const a = nm.get(e.source), b = nm.get(e.target); if (!a || !b) continue;
            const dx = b.x - a.x, dy = b.y - a.y, d = Math.sqrt(dx * dx + dy * dy) || 1;
            const f = (d - 80) * 0.05, fx = (dx / d) * f, fy = (dy / d) * f;
            if (a.fx == null) { a.vx += fx; a.vy += fy; }
            if (b.fx == null) { b.vx -= fx; b.vy -= fy; }
          }
          for (const n of nodes) {
            if (n.fx != null) continue;
            // 增强向心引力，避免节点漂离中心；核心节点（size 大）引力更强 → 自然居中
            const grav = 0.02 * (n.size || 1);
            n.vx += (cx - n.x) * grav; n.vy += (cy - n.y) * grav;
            // 速度上限，防止节点飞出
            const vmax = 10;
            n.vx = Math.max(-vmax, Math.min(vmax, n.vx));
            n.vy = Math.max(-vmax, Math.min(vmax, n.vy));
            n.vx *= 0.85; n.vy *= 0.85;
            n.x += n.vx * sim.alpha; n.y += n.vy * sim.alpha;
            n.x = Math.max(20, Math.min(width - 20, n.x)); n.y = Math.max(20, Math.min(height - 20, n.y));
          }
          sim.alpha = Math.max(0.05, sim.alpha * 0.995);
        }
        // 渲染（隐藏孤岛时过滤仅含目录边的孤立笔记）
        ctx.clearRect(0, 0, sim.width, sim.height);
        const drawNodes = hideIslandsRef.current
          ? nodes.filter((n) => n.type !== "note" || (n.linkCount || 0) > 0)
          : nodes;
        const nm = new Map(drawNodes.map((n) => [n.id, n]));
        ctx.strokeStyle = "rgba(55,65,81,0.4)"; ctx.lineWidth = 1;
        for (const e of edges) { const a = nm.get(e.source), b = nm.get(e.target); if (!a || !b) continue; ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); }
        for (const n of drawNodes) {
          const hov = hoverRef.current?.id === n.id, sel = selectedNodeRef.current?.id === n.id;
          // 球形模式下按 z 深度缩放节点（近大远小，增强 3D 球体感）
          const depthScale = layoutMode === "sphere" ? (1 + (((n as any).z ?? 0) / (Math.min(width, height) * 0.42)) * 0.6) : 1;
          // 节点大小按权重分级：size 1→4.5px（小球），size 2→9px，size 3→13.5px（核心 index 大球）
          const sizeBase = (n.size || 1) * 4.5;
          const r = (sel ? sizeBase + 3 : hov ? sizeBase + 2 : sizeBase) * depthScale;
          ctx.globalAlpha = layoutMode === "sphere" ? Math.min(1, Math.max(0.4, depthScale * 0.7)) : 1;
          ctx.shadowColor = n.color; ctx.shadowBlur = hov || sel ? 12 : 6;
          ctx.fillStyle = n.color; ctx.beginPath(); ctx.arc(n.x, n.y, r, 0, Math.PI * 2); ctx.fill();
          ctx.shadowBlur = 0; ctx.globalAlpha = 1;
          // 高连接度节点（degree>=3）或核心节点（size>=3）始终显示标签，悬停/选中节点也显示
          if (hov || sel || (n.degree || 0) >= 3 || (n.size || 1) >= 3) { ctx.fillStyle = "#E5E7EB"; ctx.font = "11px monospace"; ctx.textAlign = "center"; ctx.fillText(n.label.slice(0, 20), n.x, n.y - r - 6); }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    tick();

    const pos = (e: MouseEvent) => { const r = canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
    const find = (x: number, y: number) => { const s = simRef.current; for (let i = s.nodes.length - 1; i >= 0; i--) { const n = s.nodes[i]; const dx = n.x - x, dy = n.y - y; if (dx * dx + dy * dy < 144) return n; } return null; };

    // 具名函数便于在 cleanup 中正确移除监听器
    const onMouseDown = (e: MouseEvent) => {
      const { x, y } = pos(e);
      const n = find(x, y);
      if (n) {
        dragging = { node: n, offsetX: n.x - x, offsetY: n.y - y, moved: false, startX: x, startY: y };
        n.fx = n.x; n.fy = n.y;
        simRef.current.alpha = 1;
      }
    };
    const onMouseMove = (e: MouseEvent) => {
      const { x, y } = pos(e);
      if (dragging) {
        // 计算累积移动距离，超过 5 像素才判定为拖拽
        const dx = x - dragging.startX;
        const dy = y - dragging.startY;
        if (dx * dx + dy * dy > 25) {
          dragging.moved = true;
        }
        if (dragging.moved) {
          dragging.node.fx = x + dragging.offsetX;
          dragging.node.fy = y + dragging.offsetY;
        }
      } else {
        const n = find(x, y);
        hoverRef.current = n;
        canvas.style.cursor = n ? "pointer" : "default";
      }
    };
    const onMouseUp = () => {
      if (dragging) {
        if (!dragging.moved) {
          setSelectedNode(dragging.node);
          selectedNodeRef.current = dragging.node;
        }
        dragging.node.fx = undefined;
        dragging.node.fy = undefined;
        dragging = null;
      }
    };
    const onMouseLeave = () => {
      if (dragging) {
        dragging.node.fx = undefined;
        dragging.node.fy = undefined;
        dragging = null;
      }
      hoverRef.current = null;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "g" || e.key === "G") {
        setLayoutMode((p) => p === "force" ? "sphere" : p === "sphere" ? "grid" : "force");
        simRef.current.alpha = 1;
      }
    };

    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseup", onMouseUp);
    canvas.addEventListener("mouseleave", onMouseLeave);
    window.addEventListener("keydown", onKey);

    // cleanup 中正确移除所有监听器，避免累积
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseup", onMouseUp);
      canvas.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("keydown", onKey);
    };
  }, [rawNodes, rawEdges, layoutMode]);

  if (loading) return (<div className="h-full flex items-center justify-center text-[11px] font-mono text-gray-500">正在加载关系图谱...</div>);
  if (rawNodes.length === 0) return (<div className="h-full flex flex-col items-center justify-center gap-3"><Share2 size={32} className="text-gray-700" /><div className="text-[11px] font-mono text-gray-500">暂无图谱数据</div><button onClick={onReload} className="px-3 py-1 rounded-md border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 text-[10px] font-mono text-cyan-400 transition-colors">重新加载</button></div>);

  return (
    <div className="h-full flex flex-col rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <Share2 size={13} className="text-purple-400" />
          <span className="text-[12px] font-medium text-white">关系图谱</span>
          <span className="text-[9px] font-mono text-gray-600">{rawNodes.length} 节点 · {rawEdges.length} 连接</span>
        </div>
        <div className="flex items-center gap-1">
          {(["force", "sphere", "grid"] as const).map((m) => (
            <button key={m} onClick={() => { setLayoutMode(m); simRef.current.alpha = 1; }} className={`px-2 py-0.5 rounded text-[9px] font-mono transition-colors ${layoutMode === m ? "bg-purple-500/20 text-purple-300" : "text-gray-500 hover:text-gray-300"}`}>{m === "force" ? "力导向" : m === "sphere" ? "球形" : "网格"}</button>
          ))}
          <span className="text-gray-700 mx-1">|</span>
          {islandCount > 0 && (
            <span className="text-[9px] font-mono text-gray-600">
              {islandCount} 孤岛
            </span>
          )}
          <button
            onClick={() => setHideIslands(!hideIslands)}
            className={`px-2 py-0.5 rounded text-[9px] font-mono transition-colors ${
              hideIslands
                ? "bg-emerald-500/20 text-emerald-300"
                : "text-gray-500 hover:text-gray-300"
            }`}
            title="隐藏仅有目录归属、无任何双链/标签关联的孤立笔记（点击治理图谱凌乱）"
          >
            {hideIslands ? "显示孤岛" : "隐藏孤岛"}
          </button>
          <button onClick={onReload} className="px-2 py-0.5 rounded border border-white/[0.06] hover:bg-white/[0.04] text-[9px] font-mono text-gray-400 transition-colors">刷新</button>
        </div>
      </div>
      <div ref={containerRef} className="flex-1 relative min-h-0">
        <canvas ref={canvasRef} className="absolute inset-0" />
        <div className="absolute bottom-2 left-2 px-2 py-1 rounded bg-black/40 text-[9px] font-mono text-gray-500 pointer-events-none">拖拽节点 · 悬停查看 · 点击详情 · G 键切换布局</div>
        {selectedNode && (
          <div className="absolute top-2 right-2 w-80 max-h-[calc(100%-1rem)] rounded-lg border border-purple-500/30 bg-[#0a0e1a]/95 backdrop-blur-md p-3 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: selectedNode.color }} />
                <span className="text-[11px] font-semibold text-white truncate">{selectedNode.label}</span>
              </div>
              <button onClick={() => setSelectedNode(null)} className="text-gray-500 hover:text-white flex-shrink-0"><X size={12} /></button>
            </div>

            {/* 节点类型徽章 */}
            <div className="flex items-center gap-1.5 mb-2">
              <span
                className="text-[9px] font-mono px-1.5 py-0.5 rounded"
                style={{ background: `${selectedNode.color}20`, color: selectedNode.color }}
              >
                {selectedNode.type === 'note' ? '📄 笔记' : selectedNode.type === 'folder' ? '📁 目录' : selectedNode.type === 'tag' ? '🏷️ 标签' : selectedNode.type}
              </span>
              {selectedNode.id && selectedNode.id.includes('/') && (
                <span className="text-[9px] font-mono text-gray-600 truncate" title={selectedNode.id}>
                  {selectedNode.id}
                </span>
              )}
            </div>

            {/* 文章预览（仅 note 类型且是文件路径） */}
            {selectedNode.type === 'note' && selectedNode.id.includes('/') && !selectedNode.id.startsWith('note:') && (
              <div className="mb-2">
                <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1">📄 文章内容</div>
                {nodeContent?.loading ? (
                  <div className="flex items-center gap-1 text-[10px] text-gray-500">
                    <Loader2 size={10} className="animate-spin" /> 加载中...
                  </div>
                ) : nodeContent?.error ? (
                  <div className="text-[10px] text-red-400">{nodeContent.error}</div>
                ) : nodeContent?.content ? (
                  <pre className="text-[10px] text-gray-300 whitespace-pre-wrap break-words font-mono leading-relaxed max-h-[160px] overflow-y-auto bg-white/[0.02] rounded p-1.5 border border-white/[0.04]">
                    {nodeContent.content}
                  </pre>
                ) : null}
                {nodeContent?.content && !nodeContent?.error && (
                  <button
                    onClick={() => openFullArticle(selectedNode)}
                    className="mt-1 w-full flex items-center justify-center gap-1 px-2 py-1 text-[9px] font-mono text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded transition-colors"
                  >
                    <FileText size={9} /> 查看完整文章
                  </button>
                )}
              </div>
            )}

            {/* 关联节点列表 */}
            {adjacentNodes.length > 0 && (
              <div className="mb-1">
                <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                  🔗 关联 ({adjacentNodes.length})
                </div>
                <div className="space-y-0.5 max-h-[120px] overflow-y-auto">
                  {adjacentNodes.map((adj, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setSelectedNode(adj.node);
                        selectedNodeRef.current = adj.node;
                      }}
                      className="w-full text-left flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-white/[0.04] transition-colors group"
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ background: adj.node.color }}
                      />
                      <span className="text-[10px] text-gray-300 truncate flex-1 group-hover:text-cyan-300">
                        {adj.node.label}
                      </span>
                      <span className="text-[8px] font-mono text-gray-600 flex-shrink-0">
                        {adj.relation === 'parent' ? '属于' : adj.relation === 'link' ? '链接' : adj.relation === 'tag' ? '标签' : adj.relation}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 没有文章内容且无关联时提示 */}
            {!nodeContent?.content && adjacentNodes.length === 0 && (
              <div className="text-[10px] text-gray-600 italic">
                {selectedNode.type === 'folder' ? '点击目录节点查看其下的笔记' : selectedNode.type === 'tag' ? '点击标签节点查看带此标签的笔记' : '暂无关联文章'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 完整文章 Modal */}
      <AnimatePresence>
        {articleModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8"
            onClick={() => setArticleModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-4xl max-h-[85vh] flex flex-col rounded-xl border border-purple-500/30 bg-[#0a0e1a] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-purple-400" />
                  <h3 className="text-[14px] font-semibold text-white">{articleModal.title}</h3>
                </div>
                <button
                  onClick={() => setArticleModal(null)}
                  className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-5">
                {articleModal.loading ? (
                  <div className="flex items-center justify-center py-12 text-gray-500">
                    <Loader2 size={16} className="animate-spin mr-2" />
                    <span className="text-[13px]">正在从 Vault 加载...</span>
                  </div>
                ) : (
                  <pre className="text-[13px] text-gray-300 whitespace-pre-wrap break-words font-mono leading-relaxed">
                    {articleModal.content}
                  </pre>
                )}
              </div>
              <div className="flex items-center justify-between px-5 py-2 border-t border-white/[0.06] bg-white/[0.01]">
                <span className="text-[11px] font-mono text-gray-600">数据来源：Obsidian Vault</span>
                <span className="text-[11px] font-mono text-gray-600">点击圆点关联文章</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="px-3 py-1.5 border-t border-white/[0.06]"><span className="text-[9px] font-mono text-gray-600">数据来源：Obsidian Vault · KnowledgeQuery.Graph · 按 G 切换布局</span></div>
    </div>
  );
}

// ============================================
// 子组件：Idea 网格
// ============================================

function IdeaGrid({ ideas, onSelect }: { ideas: KnowledgeIdea[]; onSelect?: (idea: KnowledgeIdea) => void }) {
  if (ideas.length === 0) return <EmptyState message="暂无 Idea 数据" />;
  return (
    <div className="grid grid-cols-2 gap-3">
      {ideas.map((idea, idx) => (
        <motion.div
          key={idea.id || idx}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: idx * 0.03 }}
          onClick={() => onSelect?.(idea)}
          className="relative rounded-lg border border-amber-500/20 bg-amber-500/[0.02] p-3 hover:border-amber-500/40 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb size={12} className="text-amber-400" />
            <span className="text-[9px] font-mono text-amber-400 uppercase tracking-wider">
              {idea.category}
            </span>
          </div>
          <h3 className="text-[13px] font-medium text-white mb-1">{idea.title}</h3>
          <p className="text-[11px] text-gray-400 line-clamp-2">{idea.summary}</p>
        </motion.div>
      ))}
    </div>
  );
}

// ============================================
// 子组件：Research 列表
// ============================================

function ResearchList({ research }: { research: KnowledgeResearch[] }) {
  if (research.length === 0) return <EmptyState message="暂无 Research 数据" />;
  return (
    <div className="space-y-2">
      {research.map((r, idx) => (
        <motion.div
          key={r.id || idx}
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: idx * 0.03 }}
          className="relative rounded-lg border border-white/[0.06] bg-white/[0.02] p-3"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Microscope size={12} className="text-purple-400" />
              <h3 className="text-[13px] font-medium text-white">{r.title}</h3>
            </div>
            <span
              className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${
                r.status === "completed"
                  ? "text-emerald-400 bg-emerald-500/10"
                  : r.status === "in_progress"
                  ? "text-amber-400 bg-amber-500/10"
                  : "text-gray-500 bg-gray-500/10"
              }`}
            >
              {r.status}
            </span>
          </div>
          {/* 进度条 */}
          <div className="relative h-1 bg-white/[0.04] rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${r.progress}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
            />
          </div>
          <span className="text-[9px] font-mono text-gray-500 mt-1 inline-block">
            {r.progress}%
          </span>
        </motion.div>
      ))}
    </div>
  );
}

// ============================================
// 子组件：Prompt 列表
// ============================================

function PromptList({ prompts }: { prompts: KnowledgePrompt[] }) {
  if (prompts.length === 0) return <EmptyState message="暂无 Prompt 数据" />;
  return (
    <div className="grid grid-cols-2 gap-3">
      {prompts.map((p, idx) => (
        <motion.div
          key={p.id || idx}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: idx * 0.03 }}
          className="relative rounded-lg border border-emerald-500/20 bg-emerald-500/[0.02] p-3 hover:border-emerald-500/40 transition-colors"
        >
          <div className="flex items-center gap-2 mb-2">
            <MessageSquareCode size={12} className="text-emerald-400" />
            <span className="text-[9px] font-mono text-emerald-400 uppercase tracking-wider">
              {p.category}
            </span>
          </div>
          <h3 className="text-[13px] font-medium text-white mb-1">{p.name}</h3>
          <p className="text-[11px] text-gray-400 line-clamp-2">{p.description}</p>
        </motion.div>
      ))}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-12 text-[11px] font-mono text-gray-500">
      {message}
    </div>
  );
}

// ============================================
// 工具函数 + Mock 数据
// ============================================

const SUB_TABS: { id: SubTab; label: string; icon: typeof BookOpen }[] = [
  { id: "all_notes", label: "全部笔记", icon: Files },
  { id: "cards", label: "Cards", icon: FileText },
  { id: "ideas", label: "Ideas", icon: Lightbulb },
  { id: "research", label: "Research", icon: Microscope },
  { id: "prompts", label: "Prompts", icon: MessageSquareCode },
  { id: "graph", label: "Graph", icon: Share2 },
];

function extractList(resp: any, fallback: any[]): any[] {
  if (!resp || !resp.success) return fallback;
  const data = resp.data;
  if (Array.isArray(data)) return data;
  // 处理嵌套结构：KnowledgeRuntime.query() 返回 { success, data: [...] }
  // RuntimeManager.query() 再包装一层，导致 result.data = { success, data: [...] }
  if (data && typeof data === 'object') {
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.cards)) return data.cards;
    if (Array.isArray(data.ideas)) return data.ideas;
  }
  return fallback;
}

// ============================================
// KnowledgeAssetPanel - 企业知识资产统计面板（Program A Phase A）
// ============================================
// 展示：资产总量 / 今日新增 / 本周新增 / 健康分 / 各分类统计 / 每日增长趋势
// 数据来源：KnowledgeQuery.AssetStats → 扫描 09_Knowledge 下各子目录
// 降级策略：Vault 不可达时显示加载中或 0 数据

interface KnowledgeAssetPanelProps {
  stats: KnowledgeAssetStats | null;
  loading: boolean;
  onRefresh: () => void;
  searchResults: Array<{ title: string; path?: string; summary?: string; score?: number }> | null;
  onClearSearch: () => void;
  onCategoryClick?: (categoryKey: string) => void;
  /** 当前高亮的资产分类 key（让用户感知 Tab 点击生效） */
  activeAssetKey?: string | null;
}

function KnowledgeAssetPanel({
  stats,
  loading,
  onRefresh,
  searchResults,
  onClearSearch,
  onCategoryClick,
  activeAssetKey,
}: KnowledgeAssetPanelProps) {
  // 检索结果展示优先于资产统计
  if (searchResults) {
    return (
      <div className="flex-shrink-0 px-4 py-2.5 border-b border-white/[0.06] bg-cyan-500/[0.02]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Search size={12} className="text-cyan-400" />
            <span className="text-[11px] font-mono text-cyan-300">
              检索结果 · {searchResults.length} 条
            </span>
          </div>
          <button
            onClick={onClearSearch}
            className="text-[10px] font-mono text-gray-500 hover:text-gray-300"
          >
            清除
          </button>
        </div>
        <div className="max-h-[180px] overflow-y-auto space-y-1">
          {searchResults.length === 0 ? (
            <div className="text-[11px] font-mono text-gray-500 py-2 text-center">
              未找到匹配的知识
            </div>
          ) : (
            searchResults.map((r, idx) => (
              <div
                key={idx}
                className="px-2 py-1.5 rounded border border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.03] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-gray-200 truncate">
                    {r.title}
                  </span>
                  {typeof r.score === "number" && (
                    <span className="text-[9px] font-mono text-cyan-400 ml-2">
                      {(r.score * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
                {r.summary && (
                  <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-1">
                    {r.summary}
                  </p>
                )}
                {r.path && (
                  <p className="text-[9px] font-mono text-gray-600 mt-0.5">
                    {r.path}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-shrink-0 px-4 py-2.5 border-b border-white/[0.06] bg-white/[0.01]">
      {/* 顶部 KPI 行 */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Database size={11} className="text-cyan-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
            企业知识资产
          </span>
          {stats && (
            <span className="text-[9px] font-mono text-gray-600">
              · 统计于 {new Date(stats.statsAt).toLocaleTimeString("zh-CN")}
            </span>
          )}
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="flex items-center gap-1 text-[10px] font-mono text-gray-500 hover:text-cyan-400 transition-colors disabled:opacity-50"
        >
          {loading ? (
            <Loader2 size={10} className="animate-spin" />
          ) : (
            <RefreshCw size={10} />
          )}
          刷新
        </button>
      </div>

      {/* 4 个 KPI */}
      <div className="grid grid-cols-4 gap-2 mb-2">
        <AssetKpiCell
          label="资产总量"
          value={stats ? String(stats.totalAssets) : "—"}
          icon={Database}
          color="#38BDF8"
        />
        <AssetKpiCell
          label="今日新增"
          value={stats ? `+${stats.todayAdded}` : "—"}
          icon={TrendingUp}
          color="#10B981"
        />
        <AssetKpiCell
          label="本周新增"
          value={stats ? `+${stats.weekAdded}` : "—"}
          icon={Activity}
          color="#A78BFA"
        />
        <AssetKpiCell
          label="健康分"
          value={stats ? `${stats.healthScore}/100` : "—"}
          icon={Target}
          color={
            !stats
              ? "#6B7280"
              : stats.healthScore >= 70
              ? "#10B981"
              : stats.healthScore >= 40
              ? "#FBBF24"
              : "#F87171"
          }
        />
      </div>

      {/* 各分类统计（横向 chips，点击切换到对应分类，active 高亮） */}
      {stats && stats.categories.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {stats.categories.map((cat) => (
            <AssetCategoryChip
              key={cat.key}
              cat={cat}
              active={activeAssetKey === cat.key}
              onClick={() => onCategoryClick?.(cat.key)}
            />
          ))}
        </div>
      )}

      {/* 每日增长趋势（迷你条形图） */}
      {stats && stats.dailyGrowth.length > 0 && (
        <div className="mt-2 flex items-end gap-1 h-[28px]">
          {stats.dailyGrowth.map((d) => {
            const maxCount = Math.max(
              ...stats.dailyGrowth.map((g) => g.count),
              1
            );
            const height = Math.max((d.count / maxCount) * 100, 4);
            return (
              <div
                key={d.date}
                className="flex-1 flex flex-col items-center justify-end"
                title={`${d.date}: ${d.count} 篇`}
              >
                <div
                  className="w-full rounded-sm bg-cyan-500/40"
                  style={{ height: `${height}%` }}
                />
                <span className="text-[8px] font-mono text-gray-600 mt-0.5">
                  {d.date.slice(5)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {!stats && !loading && (
        <div className="text-[10px] font-mono text-gray-500 py-1 text-center">
          暂无资产统计，知识库暂未接入
        </div>
      )}
    </div>
  );
}

function AssetKpiCell({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: string;
  icon: typeof Database;
  color: string;
}) {
  return (
    <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5">
      <div className="flex items-center gap-1 mb-0.5">
        <Icon size={9} style={{ color }} />
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div
        className="text-[18px] font-mono font-semibold"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

function AssetCategoryChip({ cat, onClick, active }: { cat: KnowledgeAssetCategory; onClick?: () => void; active?: boolean }) {
  const achievementRate =
    cat.dailyTarget > 0
      ? Math.min((cat.todayAdded / cat.dailyTarget) * 100, 100)
      : 0;
  const achieved = cat.todayAdded >= cat.dailyTarget;

  return (
    <button
      onClick={onClick}
      className={`px-2 py-1 rounded-md border text-[10px] font-mono transition-colors cursor-pointer hover:scale-105 ${
        active
          ? "border-cyan-500/40 bg-cyan-500/15 text-cyan-200 shadow-[0_0_8px_rgba(34,211,238,0.3)]"
          : achieved
          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
          : "border-white/[0.06] bg-white/[0.02] text-gray-400 hover:border-cyan-500/30 hover:bg-cyan-500/[0.05]"
      }`}
      title={`${cat.vaultPath} · 今日 ${cat.todayAdded}/${cat.dailyTarget} · 点击查看`}
    >
      {active && <span className="mr-0.5">▸</span>}
      <span className={active ? "text-cyan-100" : "text-gray-300"}>{cat.label}</span>
      <span className={`ml-1.5 ${active ? "text-cyan-200" : "text-gray-200"}`}>{cat.count}</span>
      <span className="ml-1 text-gray-600">
        · 今日 {cat.todayAdded}/{cat.dailyTarget}
      </span>
      {achieved && <span className="ml-1 text-emerald-400">✓</span>}
    </button>
  );
}
